import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { UploadCloud, FileText, Loader2, Calendar, Users, ScrollText, ListChecks, AlertTriangle } from "lucide-react";

export default function Documents() {
  const navigate = useNavigate();
  const [docs, setDocs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/documents").then((r) => setDocs(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const handleFile = async (file) => {
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      toast.error("Formats acceptés : JPEG, PNG, WEBP (convertissez les PDF en image)");
      return;
    }
    if (file.size > 20 * 1024 * 1024) { toast.error("Fichier trop volumineux (max 20 Mo)"); return; }
    setUploading(true);
    try {
      const base64 = await new Promise((res, rej) => {
        const reader = new FileReader();
        reader.onload = () => res(reader.result.split(",")[1]);
        reader.onerror = rej;
        reader.readAsDataURL(file);
      });
      const r = await api.post("/documents/upload", { file_name: file.name, file_type: file.type, file_base64: base64 });
      toast.success("Document importé");
      await load();
      analyze(r.data.document_id);
    } catch {
      toast.error("Échec de l'import");
    } finally {
      setUploading(false);
    }
  };

  const analyze = async (id) => {
    setAnalyzing(true);
    setSelected({ document_id: id, analysis: null });
    try {
      const r = await api.post(`/documents/${id}/analyze`);
      setSelected(r.data);
      await load();
      toast.success("Analyse terminée");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec de l'analyse");
      setSelected(null);
      if (e.response?.status === 402) navigate("/premium");
    } finally {
      setAnalyzing(false);
    }
  };

  const openDoc = async (id) => {
    const r = await api.get(`/documents/${id}`);
    if (!r.data.analysis) analyze(id);
    else setSelected(r.data);
  };

  const a = selected?.analysis;

  return (
    <div className="p-5 sm:p-10">
      <p className="text-xs uppercase tracking-[0.2em] font-semibold text-royal mb-2">Analyse documentaire</p>
      <h1 className="font-serif text-4xl text-navy mb-8">Mes documents</h1>

      <div className="grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-5 space-y-4">
          <div
            data-testid="upload-dropzone"
            onClick={() => fileRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); handleFile(e.dataTransfer.files[0]); }}
            className="border-2 border-dashed border-royal/30 rounded-lg p-10 text-center cursor-pointer hover:border-royal hover:bg-royal/5 transition-all duration-200 bg-white"
          >
            {uploading ? (
              <Loader2 className="mx-auto text-royal animate-spin" size={32} />
            ) : (
              <UploadCloud className="mx-auto text-royal mb-3" size={32} />
            )}
            <p className="font-medium text-navy">Glissez une image ici</p>
            <p className="text-sm text-slate-500 mt-1">ou cliquez pour parcourir · JPEG, PNG · max 20 Mo</p>
            <input ref={fileRef} data-testid="file-input" type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={(e) => handleFile(e.target.files[0])} />
          </div>

          <div className="space-y-2">
            {docs.map((d) => (
              <button
                key={d.document_id}
                data-testid={`doc-${d.document_id}`}
                onClick={() => openDoc(d.document_id)}
                className={`w-full text-left bg-white border rounded-lg p-4 flex items-center gap-3 transition-all duration-200 ${selected?.document_id === d.document_id ? "border-royal" : "border-border hover:border-royal/40"}`}
              >
                <FileText size={18} className="text-royal shrink-0" />
                <div className="overflow-hidden">
                  <p className="text-sm font-medium text-navy truncate">{d.file_name}</p>
                  <p className="text-xs text-slate-400">{d.document_type || "Non analysé"}</p>
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="lg:col-span-7">
          <div className="bg-white border border-border rounded-lg p-8 min-h-[400px]">
            {!selected && <p className="text-slate-400 text-center pt-20">Importez ou sélectionnez un document pour voir l'analyse.</p>}
            {selected && analyzing && (
              <div className="text-center pt-20">
                <Loader2 className="mx-auto text-royal animate-spin mb-4" size={32} />
                <p className="text-navy font-medium">Analyse du document en cours...</p>
              </div>
            )}
            {a && (
              <div className="space-y-6">
                <div>
                  <span className="inline-block text-xs uppercase tracking-wider font-semibold text-royal bg-royal/10 px-3 py-1 rounded-full mb-3">{a.document_type}</span>
                  <p className="text-slate-700 leading-relaxed">{a.summary}</p>
                </div>
                {a.parties?.length > 0 && <Section icon={Users} title="Parties impliquées"><ul className="list-disc pl-5 text-sm text-slate-700 space-y-1">{a.parties.map((p, i) => <li key={i}>{p}</li>)}</ul></Section>}
                {a.key_dates?.length > 0 && <Section icon={Calendar} title="Dates clés">{a.key_dates.map((d, i) => <div key={i} className="flex justify-between text-sm py-1 border-b border-border last:border-0"><span className="text-slate-600">{d.label}</span><span className="font-medium text-navy">{d.date}</span></div>)}</Section>}
                {a.required_actions?.length > 0 && <Section icon={ListChecks} title="Actions requises"><ul className="list-decimal pl-5 text-sm text-slate-700 space-y-1">{a.required_actions.map((x, i) => <li key={i}>{x}</li>)}</ul></Section>}
                {a.applicable_laws?.length > 0 && <Section icon={ScrollText} title="Lois applicables">{a.applicable_laws.map((l, i) => <div key={i} className="mb-2"><p className="text-sm font-medium text-navy">{l.code} · {l.article}</p><p className="text-sm text-slate-600">{l.explanation}</p></div>)}</Section>}
                {a.recommendations?.length > 0 && <Section icon={AlertTriangle} title="Recommandations"><ul className="list-disc pl-5 text-sm text-slate-700 space-y-1">{a.recommendations.map((x, i) => <li key={i}>{x}</li>)}</ul></Section>}
                <blockquote className="border-l-3 border-gold bg-[#FBF6E9] px-4 py-3 text-sm text-[#5a4a1a] rounded-r" style={{ borderLeftWidth: 3 }}>💡 Cette analyse est informative et ne remplace pas un conseil juridique personnalisé.</blockquote>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const Section = ({ icon: Icon, title, children }) => (
  <div>
    <div className="flex items-center gap-2 text-navy mb-2"><Icon size={16} className="text-royal" /><h3 className="text-sm font-semibold uppercase tracking-wider">{title}</h3></div>
    {children}
  </div>
);
